import { useRef, useState } from 'react';
import type { PwaReviewItem, PwaReviewI18nEntry } from '@/api/types';

interface ReviewsEditorProps {
  items: PwaReviewItem[];
  i18n: Record<string, PwaReviewI18nEntry[]>;
  onItemsChange: (items: PwaReviewItem[]) => void;
  onTranslate: () => void;
  translating: boolean;
  uploadPhoto: (file: File) => Promise<string>;
}

function newReview(): PwaReviewItem {
  return {
    id: crypto.randomUUID(),
    name: '',
    photoUrl: '',
    rating: 5,
    date: '',
    text: '',
    reply: '',
    featured: false,
  };
}

/** Compact round avatar uploader — matches the storefront reviewer avatar. */
function PhotoUpload({
  value,
  onChange,
  uploadPhoto,
}: {
  value: string;
  onChange: (url: string) => void;
  uploadPhoto: (file: File) => Promise<string>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      onChange(await uploadPhoto(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="shrink-0 w-20">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        title="Upload reviewer photo"
        className="relative w-20 h-20 rounded-full overflow-hidden border border-dashed border-border-strong bg-surface-muted flex items-center justify-center hover:border-primary transition-colors"
      >
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" />
        ) : uploading ? (
          <span className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-6 h-6 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
          </svg>
        )}
      </button>
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          className="mt-1 block w-full text-center text-[11px] text-danger"
        >
          Remove
        </button>
      ) : (
        <p className="mt-1 text-center text-[11px] text-content-subtle">Photo</p>
      )}
      {error && <p className="mt-1 text-[11px] text-danger break-words">{error}</p>}
    </div>
  );
}

export default function ReviewsEditor({
  items,
  i18n,
  onItemsChange,
  onTranslate,
  translating,
  uploadPhoto,
}: ReviewsEditorProps) {
  const translatedLangCount = Object.keys(i18n || {}).length;

  const update = (index: number, patch: Partial<PwaReviewItem>) => {
    onItemsChange(items.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };
  const remove = (index: number) => onItemsChange(items.filter((_, i) => i !== index));
  const add = () => onItemsChange([...items, newReview()]);

  return (
    <div className="max-w-3xl space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-medium text-content-subtle">Reviews ({items.length})</label>
        <div className="flex items-center gap-2">
          {translatedLangCount > 0 && (
            <span className="text-xs text-content-subtle">Translated: {translatedLangCount} langs</span>
          )}
          <button
            type="button"
            onClick={onTranslate}
            disabled={translating || items.length === 0}
            className="text-xs px-2 py-1 rounded border border-border disabled:opacity-50 whitespace-nowrap"
          >
            {translating ? 'Translating…' : 'Translate to all languages'}
          </button>
        </div>
      </div>

      {items.length === 0 && (
        <p className="text-xs text-content-subtle">
          No custom reviews — the storefront shows the built-in localized samples.
        </p>
      )}

      {items.map((r, i) => (
        <div key={r.id} className="rounded-lg border border-border p-3">
          <div className="flex items-start gap-3">
            <PhotoUpload
              value={r.photoUrl || ''}
              onChange={(url) => update(i, { photoUrl: url })}
              uploadPhoto={uploadPhoto}
            />

            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  className="input flex-1 min-w-0"
                  placeholder="Reviewer name"
                  value={r.name}
                  onChange={(e) => update(i, { name: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="shrink-0 text-xs text-danger px-2 py-1 hover:underline"
                >
                  Remove
                </button>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-0.5 shrink-0 text-base leading-none">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => update(i, { rating: s })}
                      className={s <= r.rating ? 'text-yellow-400' : 'text-content-subtle'}
                      aria-label={`${s} stars`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <input
                  className="input w-44"
                  placeholder="Date (e.g. Feb 18, 2025)"
                  value={r.date}
                  onChange={(e) => update(i, { date: e.target.value })}
                />
              </div>

              <textarea
                className="input w-full"
                rows={2}
                placeholder="Review text (English)"
                value={r.text}
                onChange={(e) => update(i, { text: e.target.value })}
              />
              <textarea
                className="input w-full"
                rows={2}
                placeholder="Developer reply (optional, English)"
                value={r.reply || ''}
                onChange={(e) => update(i, { reply: e.target.value })}
              />
              <label className="flex items-center gap-2 text-xs text-content-subtle">
                <input
                  type="checkbox"
                  checked={!!r.featured}
                  onChange={(e) => update(i, { featured: e.target.checked })}
                />
                Show "Most helpful" badge
              </label>
            </div>
          </div>
        </div>
      ))}

      <button type="button" onClick={add} className="text-xs px-3 py-1.5 rounded border border-border">
        + Add review
      </button>
    </div>
  );
}
